// @ts-strict-ignore
import React, {useEffect, useRef, useState} from 'react';
import {eventWithTime} from '@rrweb/types';

import {SelectReact} from 'common/components/select';
import {SpinnerReact} from 'common/components/spinner';
import {SpinnerSize} from 'common/components/spinner/types';
import {ButtonReact} from 'common/components/button';
import {ButtonTheme} from 'common/components/button/constants';
import {Directions} from 'common/components/drop-menu/types';
import {useTranslation} from 'common/i18n/hooks';
import {useCurrentProject} from 'common/contexts/react/current-project-context';
import {reporter} from 'common/report/error-reporting';
import {isTestEnv} from 'common/report/util/env';
import {SwitchReact} from 'common/components/switch';
import {useThemes} from 'common/contexts/react/themes-context';
import {TooltipReact} from 'common/components/tooltip';
import {useLocalStorageManager} from 'common/contexts/react/storage-manager-context';
import {SkeletonList} from 'common/components/skeleton-list';
import {SkeletonListRowSize} from 'common/components/skeleton-list/types';
import {TranslationKey} from 'common/i18n';
import {getReportUrl, getWorkspaceId} from 'common/report/util';
import {isSRMultiTimelineEnabled} from 'common/report/util/feature-sampling';

import {promptFeedbackModal} from '../feedback-modal';
import {FeedbackForFeature, Sentiment} from '../feedback-modal/types';
import {CopyToClipboardButtonReact} from '../copy-to-clipboard/copy-to-clipboard-button';
import {CopyType} from '../copy-to-clipboard/copy-to-clipboard-button/types';
import styles from './index.module.styl';
import {
  EmbedErrorStateType,
  EmbedReplayParams,
  EmbedReplayPayload,
  LoadEmbedParams,
  ParentAppReplayMessage,
  RecordingEventMetadata,
  RecordingInactivePeriod,
  RecordingMetadata,
  RecordingPageViewEventMetadata,
  RecordingPlayerSource,
  RecordingPlayerTrackingEvent,
  RecordingSpeed,
  ReplayErrorStateType,
  WindowMessageRecipient,
} from './types';
import {
  fetchSignedReplayParams,
  findCurrentInactivePeriod,
  formatRecordingProgressTimeBar,
  sendEmbedReplayMessage,
  useReplayWindowMessageListener,
} from './util';
import {RecordingPlayerTimeline} from './timeline';
import {MultiTimelineContainer} from './timeline/multi-timeline-container';
import {SessionRecordingErrorState} from './error-state';

export const PLAYER_URL = REPLAY_FROM_SAME_ORIGIN ? `/replay-embed` : window[`__MP_REPLAY_EMBED_URL`];

type RecordingPlayerProps = {
  children?: React.ReactNode;
  extraPlayerControls?: React.ReactNode;
  currentRecordingEventSelectedTime: number;
  currentRecordingPaused: boolean;
  isSkipInactivityEnabled: boolean;
  replay: RecordingMetadata;
  replayEvents: RecordingEventMetadata[];
  searchFilter: string;
  replayPageViewEventsLoaded: boolean;
  replayPageViewEvents: RecordingPageViewEventMetadata[];
  hasReplayPageViewEvents: boolean;
  isStitchingEnabled: boolean;
  errorState?: EmbedErrorStateType | ReplayErrorStateType;
  isLoading?: boolean;
  currentHoveredEvent: RecordingEventMetadata | null;
  queryError?: string;
  source: RecordingPlayerSource;
  setCurrentHoveredEvent: (event: RecordingEventMetadata | null) => void;

  onFinishReplay: (replayId: string) => void;
  onHalfwayThrough: (replayId: string) => void;
  onStartReplay: (replayId: string) => void;
  onClose?: () => void;
  closeModalFn?: () => void;
  getTrackingProps?: (replayToTrack?: RecordingMetadata) => Obj;
  onToggleSkipInactivity: (isSkipInactivityEnabled: boolean) => void;
  onToggleCurrentRecordingPaused: (isPaused: boolean) => void;
  updateCurrentReplayEvent: (eventTime: number) => void;
  onTrackReplayLoaded: (props: Obj) => void;
};

function ErrorState(props: {type: EmbedErrorStateType | ReplayErrorStateType; queryError?: string}) {
  let titleKey: TranslationKey;
  let subtitleKey: TranslationKey;

  if (props.type === EmbedErrorStateType.EmbedBlocked) {
    titleKey = `session-replay.failed-to-load-player`;
    subtitleKey = `session-replay.failed-to-load-player-error`;
  } else if (props.type === ReplayErrorStateType.NoReplaysAvailable) {
    titleKey = `session-replay.no-replays-available`;
    subtitleKey = `session-replay.no-replays-available-reason`;
  } else if (props.type === ReplayErrorStateType.LoadError) {
    titleKey = `session-replay.generic-load-error`;
    subtitleKey = `session-replay.generic-load-error-details`;
  } else if (props.type === ReplayErrorStateType.NotIntegrated) {
    titleKey = `session-replay.home-upsell-title`;
    subtitleKey = `session-replay.home-upsell-troubleshooting`;
  } else {
    titleKey = `session-replay.no-replay-found`;
    subtitleKey = `session-replay.no-replay-found-error`;
  }

  return (
    <SessionRecordingErrorState
      titleKey={titleKey}
      subtitleKey={subtitleKey}
      error={props.queryError}
      type={props.type}
      subtitleLink={`https://docs.mixpanel.com/docs/session-replay/session-replay-web#faq`}
    />
  );
}

// How long before the event time to jump to when the user clicks on an event in seconds
export const EVENT_TIME_JUMP_OFFSET = 2;
// How long before the event time to jump to when the user views replays from insights in seconds
export const REPORT_EVENT_TIME_JUMP_OFFSET = 7;

const RECORDING_SPEED_STORAGE_KEY = `recordingPlayerSpeed`;

export function RecordingPlayer(props: RecordingPlayerProps) {
  const [loadingRecording, setLoadingRecording] = useState<boolean>(true);
  const [errorState, setErrorState] = useState<EmbedErrorStateType | null>(null);

  const [recordingProgress, setRecordingProgress] = useState<number>(0);
  const [recordingDuration, setRecordingDuration] = useState<number>(0);
  const [recordingFinished, setRecordingFinished] = useState<boolean>(false);
  const [recordingReachedHalf, setRecordingReachedHalf] = useState<boolean>(false);
  const [recordingSpeed, setRecordingSpeed] = useState<RecordingSpeed>(RecordingSpeed.Normal);
  const [replayInactivePeriods, setReplayInactivePeriods] = useState<RecordingInactivePeriod[]>([]);
  const [pageloadEvents, setPageloadEvents] = useState<eventWithTime[]>([]);
  const [isSkippingInactivity, setIsSkippingInactivity] = useState<boolean>(false);

  const [embedReady, setEmbedReady] = useState(false);
  const [currentURL, setCurrentURL] = useState<string>(``);

  const embedIframeRef = useRef<HTMLIFrameElement>(null);

  const currentProject = useCurrentProject();
  const {t} = useTranslation();
  const themesResource = useThemes();
  const localStorage = useLocalStorageManager();

  const isMultiTimelineEnabled = isSRMultiTimelineEnabled(currentProject.get()?.value);
  const hasError = Boolean(props.errorState || errorState);

  useEffect(() => {
    if (props.replay?.replayId) {
      sendReplayParams(props.replay);
    }
  }, [props.replay?.replayId, embedReady]);

  useEffect(() => {
    if (props.currentRecordingEventSelectedTime && embedIframeRef.current && recordingDuration) {
      goToEventTime();
    }
  }, [props.currentRecordingEventSelectedTime, embedIframeRef.current, recordingDuration]);

  useEffect(() => {
    if (embedReady && embedIframeRef.current) {
      sendEmbedMessage({isSkipInactivityEnabled: props.isSkipInactivityEnabled});
    }
  }, [props.isSkipInactivityEnabled, embedReady]);

  useEffect(() => {
    if (embedIframeRef.current) {
      if (recordingFinished) {
        setRecordingFinished(false);
        setRecordingProgress(0);
        sendEmbedMessage({recordingPaused: false});
      } else {
        sendEmbedMessage({recordingPaused: props.currentRecordingPaused});
      }
    }
  }, [props.currentRecordingPaused]);

  useEffect(() => {
    if (props.isLoading) {
      sendEmbedMessage({recordingPaused: true});
    }
  }, [props.isLoading]);

  useEffect(() => {
    setRecordingSpeed(getSavedReplaySpeed());
  }, []);

  useReplayWindowMessageListener<ParentAppReplayMessage>(WindowMessageRecipient.ParentApp, window, (message) => {
    const payload = message.payload;

    if (payload.embedReady) {
      setEmbedReady(true);
    }

    if (payload.embedBlocked) {
      setErrorState(EmbedErrorStateType.EmbedBlocked);
    }

    if (payload.recordingProgress) {
      setRecordingProgress(payload.recordingProgress);
      if (props.replay) {
        props.updateCurrentReplayEvent(payload.recordingProgress / 1000 + props.replay.time);
      }
      findCurrentURL(payload.recordingProgress);
    }

    if (payload.recordingDuration) {
      setRecordingDuration(payload.recordingDuration);
    }

    if (`loadingRecording` in payload) {
      setLoadingRecording(payload.loadingRecording);
    }

    if (
      recordingDuration > 0 &&
      !recordingReachedHalf &&
      !props.currentRecordingPaused &&
      payload.recordingProgress / recordingDuration >= 0.5
    ) {
      setRecordingReachedHalf(true);
      props.onHalfwayThrough(props.replay.replayId);
    }

    if (payload.recordingStarted) {
      setRecordingFinished(false);
      setRecordingReachedHalf(false);
      props.onToggleCurrentRecordingPaused(false);
      setLoadingRecording(false);
    }

    if (payload.recordingFinished) {
      setRecordingFinished(true);
      props.onFinishReplay(props.replay.replayId);
    }

    if (payload.recordingInactivePeriods) {
      setReplayInactivePeriods(payload.recordingInactivePeriods);
    }

    if (payload.pageloadEvents) {
      setPageloadEvents(payload.pageloadEvents);
    }

    if (payload.error) {
      reporter.error(payload.error);
      setErrorState(EmbedErrorStateType.NoReplayFound);
    }

    if (payload.playerClicked) {
      toggleRecording();
    }

    if (payload.trackReplayLoaded) {
      props.onTrackReplayLoaded(payload.trackReplayLoaded);
    }

    if (`isSkippingInactivity` in payload) {
      setIsSkippingInactivity(Boolean(payload.isSkippingInactivity));
    }
  });

  async function sendReplayParams(replay: RecordingMetadata) {
    setLoadingRecording(true);
    setIsSkippingInactivity(false);
    const signedParams = await fetchSignedReplayParams(replay.replayId, replay.replayEnv);

    const replayParams: EmbedReplayParams = {
      signedParams,
      retentionPeriod: replay.retentionPeriod,
      checkpointStartTime: replay.time,
      replayId: replay.replayId,
    };
    const embedParams: LoadEmbedParams = {
      replayParams: [replayParams],
      targetOrigin: window.location.origin,
    };

    sendEmbedMessage({
      embedParams,
      isSkipInactivityEnabled: props.isSkipInactivityEnabled,
      recordingSpeed,
    });
    setRecordingFinished(false);
    props.onStartReplay(replay.replayId);
    setLoadingRecording(false);
  }

  function findCurrentURL(progress: number) {
    let newCurrentURL = props.replay?.startingURL || t(`session-replay.unknown-starting-url`);

    if (props.replay && props.replayPageViewEvents) {
      [...props.replayPageViewEvents]
        .sort((a, b) => a.eventTime - b.eventTime)
        .forEach((event) => {
          if (event.eventTime <= props.replay.time + progress / 1000) {
            newCurrentURL = event.currentURL;
          }
        });
    }

    setCurrentURL(newCurrentURL);
  }

  function getSavedReplaySpeed(): RecordingSpeed {
    const savedSpeed = localStorage.getItem(RECORDING_SPEED_STORAGE_KEY);
    if (savedSpeed) {
      return Number(savedSpeed) as RecordingSpeed;
    }
    return RecordingSpeed.Normal;
  }

  function saveReplaySpeed(speed: RecordingSpeed) {
    localStorage.setItem(RECORDING_SPEED_STORAGE_KEY, speed.toString());
  }

  function sendEmbedMessage(payload: EmbedReplayPayload) {
    if (embedIframeRef.current) {
      sendEmbedReplayMessage(
        {...payload, isDarkMode: themesResource.isDarkMode()},
        embedIframeRef.current.contentWindow,
      );
    }
  }

  function goToEventTime() {
    if (props.currentRecordingEventSelectedTime && embedIframeRef.current && recordingDuration && props.replay) {
      const targetEventTime = props.currentRecordingEventSelectedTime - EVENT_TIME_JUMP_OFFSET;
      const timeFromRecordingStart = Math.max(targetEventTime - props.replay.time, 0);
      goToTime(timeFromRecordingStart * 1000);
    }
  }

  function goToTime(progressMs: number) {
    setRecordingProgress(progressMs);
    const embedMessage: EmbedReplayPayload = {
      recordingProgress: progressMs,
      recordingPaused: props.currentRecordingPaused,
    };

    // if the user jumps to an inactive period, disable skip inactivity
    const isInactivePeriod = !!findCurrentInactivePeriod(progressMs + props.replay.time * 1000, replayInactivePeriods);
    if (isInactivePeriod) {
      embedMessage.isSkipInactivityEnabled = false;
      props.onToggleSkipInactivity(false);
    }

    if (progressMs < recordingDuration) {
      setRecordingFinished(false);
    }
    findCurrentURL(recordingProgress);

    sendEmbedMessage(embedMessage);
  }

  function toggleRecording() {
    if (embedIframeRef.current) {
      if (recordingFinished) {
        setRecordingFinished(false);
        props.onToggleCurrentRecordingPaused(false);
        setRecordingProgress(0);
        sendEmbedMessage({recordingPaused: false});
      } else {
        props.onToggleCurrentRecordingPaused(!props.currentRecordingPaused);
        sendEmbedMessage({recordingPaused: !props.currentRecordingPaused});
      }
    }
  }

  function updatePlayerSpeed(newSpeed: RecordingSpeed) {
    if (embedIframeRef.current) {
      setRecordingSpeed(newSpeed);
      sendEmbedMessage({recordingSpeed: newSpeed});
      saveReplaySpeed(newSpeed);
    }
  }

  function showFeedbackModal(sentiment: Sentiment) {
    promptFeedbackModal(
      {
        feature: FeedbackForFeature.SessionReplay,
        'negative-label': ``,
        placeholder: t(`session-replay.feedback-placeholder`),
        'sentiment-heading': t(`session-replay.feedback-description`),
        'positive-label': ``,
        title: t(`session-replay.feedback`),
      },
      {
        extraFeedbackEventName: RecordingPlayerTrackingEvent.Feedback,
        extraFeedbackEventProperties: props.getTrackingProps?.() || {},
        initialSentiment: sentiment,
      },
    );
  }

  function getReplayAreaState(closeModalFn?: () => void) {
    return (
      <div className={styles.feedbackContainer}>
        <ButtonReact
          theme={ButtonTheme.Tertiary}
          icon='thumbs-up'
          onClick={() => showFeedbackModal(Sentiment.Positive)}
          tooltip={{text: t(`session-replay.feedback-tooltip`)}}
        />
        <ButtonReact
          theme={ButtonTheme.Tertiary}
          icon='thumbs-down'
          onClick={() => showFeedbackModal(Sentiment.Negative)}
          tooltip={{text: t(`session-replay.feedback-tooltip`)}}
        />
        {closeModalFn && props.source !== RecordingPlayerSource.ReplayHome && (
          <ButtonReact theme={ButtonTheme.Tertiary} icon='x' onClick={() => closeModalFn()} />
        )}
      </div>
    );
  }

  const formatRecordingProgressTimeBarResult = formatRecordingProgressTimeBar(recordingProgress, recordingDuration);
  const currentURLValue = currentURL || t(`session-replay.unknown-starting-url`);

  return (
    <div className={styles.recordingContent}>
      {!hasError && (
        <div className={styles.recordingHeader}>
          <div className={styles.recordingUrlDisplay}>
            <CopyToClipboardButtonReact
              className={styles.recordingUrlDisplayIcon}
              copyType={CopyType.Link}
              options={[
                {
                  value: currentURLValue,
                },
              ]}
              theme={ButtonTheme.Tertiary}
              disabled={!props.replayPageViewEventsLoaded || props.replay?.source !== `web`}
            />
            {!props.replayPageViewEventsLoaded || !props.replay ? (
              <SkeletonList
                className={styles.recordingUrlDisplaySkeleton}
                numRows={1}
                rowSize={SkeletonListRowSize.Large}
              />
            ) : (
              <div className={styles.recordingUrlDisplayText}>
                {currentURLValue}
                <TooltipReact text={currentURLValue} />
              </div>
            )}
            {!props.hasReplayPageViewEvents && <TooltipReact text={t(`session-replay.no-page-view-events`)} />}
          </div>
          {currentURL && (
            <ButtonReact
              icon='heat-map'
              disabled={props.replay?.source !== `web`}
              onClick={() => {
                props.onClose?.();
                const newSearchParams = new URLSearchParams();
                newSearchParams.append(`session-replay-url`, encodeURIComponent(currentURLValue));
                const path = getReportUrl(`heatmaps/`, {
                  projectId: currentProject.id,
                  workspaceId: parseInt(getWorkspaceId()),
                });
                const heatmapUrl = `${path}?${newSearchParams}`;
                window.open(heatmapUrl, `_blank`);
              }}
            >
              {t(`heat-maps.view-heat-map`)}
            </ButtonReact>
          )}
          {getReplayAreaState(props.closeModalFn || (() => {}))}
        </div>
      )}
      <div
        className={styles.playerIframeContainer}
        role={`region`}
        style={loadingRecording || hasError || props.isLoading ? {visibility: `hidden`} : {}}
        onError={(error) => {
          if (currentProject.get().value?.id === 3 || currentProject.get().value?.id === 132990) {
            error.preventDefault();
          }
        }}
        onClick={() => toggleRecording()}
      >
        {/* interactions with the actual player must be tested separately */}
        {!isTestEnv() && <iframe className={styles.playerIframe} src={PLAYER_URL} ref={embedIframeRef} />}
        {isSkippingInactivity && (
          <div className={styles.skipInactivityOverlay}>
            <div className={styles.skipInactivityOverlayText}>{t(`session-replay.skipping-inactivity`)}</div>
          </div>
        )}
      </div>
      <div className={styles.playerControls}>
        {embedIframeRef.current && !hasError && !props.isLoading && (
          <div className={styles.playerControlsSpace}>
            <div className={styles.playerControlsProgressBarArea}>
              {isMultiTimelineEnabled ? (
                <MultiTimelineContainer
                  replay={props.replay}
                  replayEvents={props.replayEvents}
                  recordingDuration={recordingDuration}
                  recordingProgress={recordingProgress}
                  replayInactivePeriods={replayInactivePeriods}
                  pageloadEvents={pageloadEvents}
                  searchFilter={props.searchFilter}
                  onUpdatePlayerProgress={goToTime}
                  isStitchingEnabled={props.isStitchingEnabled}
                  currentHoveredEvent={props.currentHoveredEvent}
                  setCurrentHoveredEvent={props.setCurrentHoveredEvent}
                />
              ) : (
                <RecordingPlayerTimeline
                  replay={props.replay}
                  replayEvents={props.replayEvents}
                  recordingDuration={recordingDuration}
                  recordingProgress={recordingProgress}
                  replayInactivePeriods={replayInactivePeriods}
                  pageloadEvents={pageloadEvents}
                  searchFilter={props.searchFilter}
                  onUpdatePlayerProgress={goToTime}
                  isStitchingEnabled={props.isStitchingEnabled}
                  currentHoveredEvent={props.currentHoveredEvent}
                  setCurrentHoveredEvent={props.setCurrentHoveredEvent}
                />
              )}
            </div>
            <div className={styles.playerControlsArea}>
              <div className={styles.playerControlsAreaLeft}>
                <ButtonReact
                  className={styles.primaryButton}
                  icon={recordingFinished ? `restart` : props.currentRecordingPaused ? `play` : `pause`}
                  onClick={() => toggleRecording()}
                  theme={ButtonTheme.Primary}
                />
                <SelectReact
                  className={styles.playerControlsAreaSelect}
                  sections={[
                    {
                      items: [
                        {value: RecordingSpeed.Half, label: `0.5x`},
                        {value: RecordingSpeed.ThreeQuarters, label: `0.75x`},
                        {value: RecordingSpeed.Normal, label: `1x`},
                        {value: RecordingSpeed.Double, label: `2x`},
                        {value: RecordingSpeed.Quadruple, label: `4x`},
                        {value: RecordingSpeed.Octuple, label: `8x`},
                        {value: RecordingSpeed.Dectuple, label: `10x`},
                      ],
                    },
                  ]}
                  selectedOption={`${recordingSpeed}x`}
                  onChange={(event) => updatePlayerSpeed(event.detail.item.value)}
                  menuAlignment={Directions.Up}
                  menuWidth={null}
                  menuHeight={305}
                />
                <div className={styles.skipInactivity}>
                  <SwitchReact
                    on={props.isSkipInactivityEnabled}
                    onChange={(ev) => {
                      props.onToggleSkipInactivity(ev.detail.on);
                    }}
                  />
                  <span>Skip Inactivity</span>
                </div>
              </div>
              <div>{`${formatRecordingProgressTimeBarResult.formattedProgress} / ${formatRecordingProgressTimeBarResult.formattedDuration}`}</div>
              <div className={styles.playerControlsAreaRight}>{props.extraPlayerControls}</div>
            </div>
          </div>
        )}
      </div>
      {hasError ? (
        <ErrorState type={errorState || props.errorState} queryError={props.queryError} />
      ) : props.isLoading || loadingRecording ? (
        <SpinnerReact className={styles.loadingSpinner} size={SpinnerSize.Large} role={`progressbar`} />
      ) : null}
      {props.children}
    </div>
  );
}
